import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Search } from 'lucide-react';
import { useAdminModules, useAdminCategories } from '@/hooks/useEducationAdmin';
import { ModuleCard } from './components/ModuleCard';
import { ContentEditDialog } from './ContentEditDialog';
import { SlideManager } from './SlideManager';
import { QuizQuestionManager } from './QuizQuestionManager';
import { AdminEducationModule } from '@/hooks/useEducationAdmin';

export const ContentManagement: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [slideManagerOpen, setSlideManagerOpen] = useState(false);
  const [quizManagerOpen, setQuizManagerOpen] = useState(false);
  const [editingModule, setEditingModule] = useState<AdminEducationModule | null>(null);
  const [selectedModuleId, setSelectedModuleId] = useState<string>('');

  const { data: categories = [] } = useAdminCategories();
  const { data: modules = [], isLoading } = useAdminModules(
    selectedCategory === 'all' ? undefined : selectedCategory
  );

  const filteredModules = modules.filter(module =>
    module.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (module.description && module.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleCreateModule = () => {
    setEditingModule(null);
    setEditDialogOpen(true);
  };

  const handleEditModule = (module: AdminEducationModule) => {
    setEditingModule(module);
    setEditDialogOpen(true);
  };

  const handleManageSlides = (module: AdminEducationModule) => {
    setSelectedModuleId(module.id);
    setSlideManagerOpen(true);
  };

  const handleManageQuiz = (module: AdminEducationModule) => {
    setSelectedModuleId(module.id);
    setQuizManagerOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Module Management</h2>
          <p className="text-muted-foreground">
            Create and manage educational modules, slides, and quiz content.
          </p>
        </div>
        <Button onClick={handleCreateModule}>
          <Plus className="mr-2 h-4 w-4" />
          Create Module
        </Button>
      </div>

      <div className="flex items-center space-x-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search modules..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filter by course" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Courses</SelectItem>
            {categories.map((category) => (
              <SelectItem key={category.id} value={category.id}>
                {category.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {filteredModules.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground">
            {searchTerm || selectedCategory !== 'all' 
              ? 'No modules found matching your filters.' 
              : 'No modules found. Create your first module to get started.'}
          </p>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredModules.map((module) => (
            <ModuleCard
              key={module.id}
              module={module}
              onEdit={handleEditModule}
              onManageSlides={handleManageSlides}
              onManageQuiz={handleManageQuiz}
            />
          ))}
        </div>
      )}

      <ContentEditDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        module={editingModule}
      />

      <SlideManager
        open={slideManagerOpen}
        onOpenChange={setSlideManagerOpen}
        moduleId={selectedModuleId}
      />

      <QuizQuestionManager
        open={quizManagerOpen}
        onOpenChange={setQuizManagerOpen}
        moduleId={selectedModuleId}
      />
    </div>
  );
};